// Facturation : lecture du miroir d'abonnement que renvoie le backend
// (`billing` dans le profil pour le bandeau, GET /stripe/billing pour la page).
// Tout ce qui s'affiche vient de l'abonnement réel de l'enseignant — le prix
// qu'il paie, sa carte — jamais du tarif en vigueur.

export interface BillingCard {
	brand: string | null;
	last4: string;
	exp_month: number | null;
	exp_year: number | null;
}

export interface BillingInfo {
	status: string;
	price_id: string;
	unit_amount: number | null;
	interval: 'month' | 'year' | null;
	current_period_end: string | null;
	cancel_at: string | null;
	unpaid_invoice_url: string | null;
	card: BillingCard | null;
}

export type BillingAlert =
	| { kind: 'impaye'; suspended: boolean; url: string | null }
	| { kind: 'carte'; expired: boolean; expiration: string };

// Une carte est signalée quand elle expire dans moins de 30 jours : c'est le
// délai que Stripe laisse aussi à ses propres rappels.
export const CARD_WARNING_DAYS = 30;

const STATUTS_PAYANTS = ['active', 'trialing'];

/** Dernier instant de validité de la carte : la fin de son mois d'expiration. */
export function cardExpiryDate(card: BillingCard): Date | null {
	if (!card.exp_month || !card.exp_year) return null;
	return new Date(card.exp_year, card.exp_month, 0, 23, 59, 59);
}

export function billingAlert(
	billing: BillingInfo | null | undefined,
	now: Date = new Date()
): BillingAlert | null {
	if (!billing) return null;
	if (billing.status === 'past_due' || billing.status === 'unpaid') {
		return {
			kind: 'impaye',
			suspended: billing.status === 'unpaid',
			url: billing.unpaid_invoice_url
		};
	}
	// Un abonnement résilié en fin de période ne sera plus prélevé : sa carte
	// peut expirer sans conséquence.
	if (billing.card && STATUTS_PAYANTS.includes(billing.status)) {
		const expiry = cardExpiryDate(billing.card);
		if (expiry) {
			const days = (expiry.getTime() - now.getTime()) / 86_400_000;
			if (days < CARD_WARNING_DAYS) {
				return { kind: 'carte', expired: days < 0, expiration: formatExpiration(billing.card) };
			}
		}
	}
	return null;
}

export function formatPrix(cents: number): string {
	const euros = cents / 100;
	return euros.toLocaleString('fr-FR', {
		style: 'currency',
		currency: 'EUR',
		minimumFractionDigits: Number.isInteger(euros) ? 0 : 2,
		maximumFractionDigits: 2
	});
}

export function libelleFormule(billing: BillingInfo): string {
	if (billing.interval === 'year') return 'Pro annuel';
	if (billing.interval === 'month') return 'Pro mensuel';
	return 'Pro';
}

export function libellePrix(billing: BillingInfo): string {
	if (billing.unit_amount == null) return '—';
	const prix = formatPrix(billing.unit_amount);
	if (billing.interval === 'year') return `${prix} par an`;
	if (billing.interval === 'month') return `${prix} par mois`;
	return prix;
}

export function formatDate(iso: string): string {
	return new Date(iso).toLocaleDateString('fr-FR', {
		day: 'numeric',
		month: 'long',
		year: 'numeric'
	});
}

const MARQUES: Record<string, string> = {
	visa: 'Visa',
	mastercard: 'Mastercard',
	amex: 'American Express',
	cartes_bancaires: 'Carte bancaire',
	discover: 'Discover',
	diners: 'Diners Club',
	jcb: 'JCB',
	unionpay: 'UnionPay'
};

export function libelleCarte(card: BillingCard): string {
	const marque = (card.brand && MARQUES[card.brand]) || 'Carte';
	return `${marque} •••• ${card.last4}`;
}

export function formatExpiration(card: BillingCard): string {
	if (!card.exp_month || !card.exp_year) return '';
	return `${String(card.exp_month).padStart(2, '0')}/${card.exp_year}`;
}
